import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Minus, Plus, RotateCcw, Mail } from "lucide-react";
import { compute, currency, percent, DEFAULT_INPUTS, RESET_CTA_URL, LOGO_URL } from "../lib/calc";
import { generateReport } from "../lib/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pre-load logo as data URL for the PDF (so jsPDF can embed it)
async function fetchLogoDataUrl() {
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

export default function Calculator({ lead }) {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [emailToast, setEmailToast] = useState(null);

  useEffect(() => {
    fetchLogoDataUrl().then(setLogoDataUrl);
  }, []);

  const r = useMemo(() => compute(inputs), [inputs]);

  const update = (key) => (e) => {
    const v = e.target.type === "number" || e.target.type === "range"
      ? e.target.value === "" ? "" : Number(e.target.value)
      : e.target.value;
    setInputs((p) => ({ ...p, [key]: v }));
  };

  const stepExtra = (delta) => {
    setInputs((p) => ({
      ...p,
      extraSavingsPercent: Math.min(15, Math.max(1, Number(p.extraSavingsPercent || 1) + delta)),
    }));
  };

  const reset = () => setInputs(DEFAULT_INPUTS);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      // 1) Trigger immediate browser download (jsPDF, instant feedback)
      generateReport(inputs, r, lead, logoDataUrl);

      // 2) Fire-and-show: also email the PDF via backend (server-side ReportLab + Resend)
      if (lead?.email) {
        setEmailToast({ msg: "Emailing your report…", kind: "info" });
        try {
          await axios.post(`${API}/email-report`, {
            lead_id: lead.id || null,
            name: lead.name || null,
            email: lead.email,
            inputs,
          });
          setEmailToast({
            msg: `Sent! Check ${lead.email} for your PDF report.`,
            kind: "success",
          });
        } catch (err) {
          const detail = err?.response?.data?.detail || "Email send failed.";
          setEmailToast({
            msg: `Downloaded, but couldn't email: ${detail}`,
            kind: "error",
          });
        }
        setTimeout(() => setEmailToast(null), 6000);
      }
    } finally {
      setTimeout(() => setDownloadingPdf(false), 500);
    }
  };

  const totalPctRounded = Math.round(r.totalPct);
  const budgetWarning =
    totalPctRounded !== 100
      ? `Your percentages currently add up to ${percent(r.totalPct, 0)}. We normalized them automatically so the full take-home pay still gets allocated.`
      : "";

  return (
    <div className="fl-wrap">
      {/* Hero */}
      <section className="fl-hero">
        <div className="fl-fade-in">
          <span className="badge" data-testid="fl-hero-badge">
            Finlightenment Financial Fitness Check
          </span>
          <h1>A one-stop snapshot of your budget, your future, and your next %.</h1>
          <p className="subhead">
            See how healthy your current spending is, how you are positioning yourself for retirement,
            and how a small shift in your budget could strengthen your future with surprisingly little impact on your current take-home pay.
          </p>
        </div>
        <div className="fl-impact-card fl-fade-in delay-1">
          <div className="eyebrow">Your % opportunity</div>
          <div className="big" data-testid="fl-hero-extra-impact">{currency(r.extraImpactToday)}</div>
          <p data-testid="fl-hero-impact-text">
            Look at the impact you can create — by building a strong budgeting habit and redirecting{" "}
            <strong>{r.extraSavingsPercent}% more</strong> toward your future, you can meaningfully change your retirement outcome.
          </p>
          <div className="fl-stepper" aria-label="Adjust additional savings percent">
            <button type="button" onClick={() => stepExtra(-1)} aria-label="Decrease percent" data-testid="fl-percent-down"><Minus size={16} /></button>
            <span data-testid="fl-percent-value">{r.extraSavingsPercent}% more</span>
            <button type="button" onClick={() => stepExtra(1)} aria-label="Increase percent" data-testid="fl-percent-up"><Plus size={16} /></button>
          </div>
        </div>
      </section>

      <section className="fl-main-grid">
        {/* Inputs panel */}
        <div className="fl-card fl-panel fl-fade-in" data-testid="fl-inputs-panel">
          <h2>Your inputs</h2>
          <p className="fl-panel-sub">
            A few simple inputs give you a clear snapshot of where you are now, where you may be headed, and what a small shift could change.
          </p>

          <div className="fl-mini-section">About you</div>
          <div className="fl-two-col">
            <div className="fl-field">
              <label htmlFor="age">Current age</label>
              <input id="age" className="fl-num" type="number" min="18" max="90" value={inputs.age} onChange={update("age")} data-testid="fl-input-age" />
            </div>
            <div className="fl-field">
              <label htmlFor="retirementAge">Retirement age</label>
              <input id="retirementAge" className="fl-num" type="number" min="18" max="90" value={inputs.retirementAge} onChange={update("retirementAge")} data-testid="fl-input-retirement-age" />
            </div>
          </div>

          <div className="fl-mini-section">Your money now</div>
          <div className="fl-field">
            <label htmlFor="grossIncome">Gross annual income</label>
            <input id="grossIncome" className="fl-num" type="number" min="0" step="1000" value={inputs.grossIncome} onChange={update("grossIncome")} data-testid="fl-input-gross-income" />
          </div>
          <div className="fl-field">
            <label htmlFor="takeHomePay">Annual take-home pay</label>
            <input id="takeHomePay" className="fl-num" type="number" min="0" step="1000" value={inputs.takeHomePay} onChange={update("takeHomePay")} data-testid="fl-input-take-home" />
          </div>
          <div className="fl-field">
            <label htmlFor="currentBalance">Current retirement balance</label>
            <input id="currentBalance" className="fl-num" type="number" min="0" step="1000" value={inputs.currentBalance} onChange={update("currentBalance")} data-testid="fl-input-current-balance" />
          </div>

          <div className="fl-muted-box">
            <p style={{ margin: "0 0 4px" }}>
              <strong>Take-home pay as % of gross:</strong>{" "}
              <span data-testid="fl-net-ratio">{percent(r.estimatedNetRatio, 1)}</span>
            </p>
            <span className="fl-small">This helps you anchor your budget to what is actually landing in your bank account.</span>
          </div>

          <div className="fl-divider" />

          <div className="fl-mini-section">Retirement savings</div>
          <div>
            <div className="fl-range-head">
              <label htmlFor="retirementSavingRate" style={{ margin: 0, fontWeight: 700 }}>
                Current savings rate <span style={{ fontWeight: 500, color: "var(--muted)" }}>(of gross)</span>
              </label>
              <span className="val" data-testid="fl-saving-rate-value">{percent(inputs.retirementSavingRate, 1)}</span>
            </div>
            <input
              id="retirementSavingRate"
              className="fl-range"
              type="range" min="0" max="30" step="0.5"
              value={inputs.retirementSavingRate}
              onChange={update("retirementSavingRate")}
              data-testid="fl-input-saving-rate"
            />
            <div className="fl-small" style={{ marginTop: 6 }}>
              Calculated from your <strong>gross income</strong>. Your budget percentages below are based on <strong>take-home pay</strong>, which is why these may not match exactly.
            </div>
          </div>

          <a className="fl-toggle-link" onClick={() => setShowAssumptions((v) => !v)} data-testid="fl-toggle-assumptions">
            {showAssumptions ? "Hide assumptions" : "Adjust assumptions"}
          </a>
          <div className={`fl-assumptions ${showAssumptions ? "open" : ""}`}>
            <div className="fl-field">
              <label htmlFor="nominalReturn">Return %</label>
              <input id="nominalReturn" className="fl-num" type="number" min="0" max="15" step="0.1" value={inputs.nominalReturn} onChange={update("nominalReturn")} data-testid="fl-input-return" />
              <details><summary>Typical range</summary><div className="fl-small">Usually 5–8% for a diversified long-term portfolio.</div></details>
            </div>
            <div className="fl-field">
              <label htmlFor="inflation">Inflation %</label>
              <input id="inflation" className="fl-num" type="number" min="0" max="10" step="0.1" value={inputs.inflation} onChange={update("inflation")} data-testid="fl-input-inflation" />
              <details><summary>Typical range</summary><div className="fl-small">Long-term averages are typically 2–4%.</div></details>
            </div>
            <div className="fl-field">
              <label htmlFor="incomeGrowth">Income growth %</label>
              <input id="incomeGrowth" className="fl-num" type="number" min="0" max="10" step="0.1" value={inputs.incomeGrowth} onChange={update("incomeGrowth")} data-testid="fl-input-income-growth" />
              <details><summary>Typical range</summary><div className="fl-small">Often falls between 1–4% depending on career growth.</div></details>
            </div>
            <div className="fl-field" style={{ marginTop: 6 }}>
              <label htmlFor="marginalTaxRate">Estimated marginal tax rate %</label>
              <input id="marginalTaxRate" className="fl-num" type="number" min="0" max="50" step="1" value={inputs.marginalTaxRate} onChange={update("marginalTaxRate")} data-testid="fl-input-tax-rate" />
            </div>
          </div>
        </div>

        {/* Results column */}
        <div>
          <div className="fl-stats-grid">
            <div className="fl-stat fl-fade-in">
              <div className="label">Future Allocation</div>
              <div className="value" data-testid="fl-stat-future-annual">{currency(r.futureAnnual)}</div>
              <div className="desc">About {currency(r.futureMonthly)}/month toward future goals using your current Future % setting.</div>
            </div>
            <div className="fl-stat fl-fade-in delay-1">
              <div className="label">Projected Value</div>
              <div className="value" data-testid="fl-stat-current-today">{currency(r.currentToday)}</div>
              <div className="desc">Estimated in today's dollars by age {inputs.retirementAge}.</div>
            </div>
            <div className="fl-stat fl-fade-in delay-2">
              <div className="label">Savings Rate</div>
              <div className="value">{percent(inputs.retirementSavingRate, 1)}</div>
              <div className="desc">{currency(r.currentAnnualContribution)} saved annually from your current savings rate.</div>
            </div>
            <div className="fl-stat gold-accent fl-fade-in delay-3">
              <div className="label">Value of +{r.extraSavingsPercent}%</div>
              <div className="value" data-testid="fl-stat-extra-impact">{currency(r.extraImpactToday)}</div>
              <div className="desc">Additional projected value in today's dollars.</div>
            </div>
          </div>

          <div className="fl-feature-grid">
            <div className="fl-card fl-section-card">
              <div className="fl-section-title">1) How are you doing now?</div>
              <p className="fl-panel-sub" style={{ marginBottom: 14 }}>
                Start with this guide, then compare it to your real budget. Small shifts from Needs or Wants toward Future can create meaningful long-term change.
              </p>
              <div className="fl-three-col">
                <div className="fl-mini-card">
                  <div className="label">Needs</div>
                  <div className="value" data-testid="fl-needs-annual">{currency(r.needsAnnual)}</div>
                  <div className="desc">{currency(r.needsMonthly)}/mo</div>
                </div>
                <div className="fl-mini-card">
                  <div className="label">Wants</div>
                  <div className="value" data-testid="fl-wants-annual">{currency(r.wantsAnnual)}</div>
                  <div className="desc">{currency(r.wantsMonthly)}/mo</div>
                </div>
                <div className="fl-mini-card">
                  <div className="label">Future</div>
                  <div className="value" data-testid="fl-future-annual">{currency(r.futureAnnual)}</div>
                  <div className="desc">{currency(r.futureMonthly)}/mo</div>
                </div>
              </div>

              <div className="fl-budget-grid">
                <div className="fl-mini-card">
                  <label htmlFor="needsPct" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: "var(--muted)", textTransform: "uppercase" }}>Needs %</label>
                  <input id="needsPct" className="fl-num" type="number" min="0" max="100" step="1" value={inputs.needsPct} onChange={update("needsPct")} data-testid="fl-input-needs-pct" />
                </div>
                <div className="fl-mini-card">
                  <label htmlFor="wantsPct" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: "var(--muted)", textTransform: "uppercase" }}>Wants %</label>
                  <input id="wantsPct" className="fl-num" type="number" min="0" max="100" step="1" value={inputs.wantsPct} onChange={update("wantsPct")} data-testid="fl-input-wants-pct" />
                </div>
                <div className="fl-mini-card">
                  <label htmlFor="futurePct" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: "var(--muted)", textTransform: "uppercase" }}>Future %</label>
                  <input id="futurePct" className="fl-num" type="number" min="0" max="100" step="1" value={inputs.futurePct} onChange={update("futurePct")} data-testid="fl-input-future-pct" />
                </div>
              </div>

              <div className="fl-budget-note">Compare this guide to your actual budget to see how close you are in real life.</div>
              <div className="fl-soft-prompt">Try increasing Future by just 1–3% to see the impact.</div>
              {budgetWarning && <div className="fl-budget-warning" data-testid="fl-budget-warning">{budgetWarning}</div>}

              <div className="fl-toggle-card">
                <label htmlFor="futureIncludesRetirement">Does your Future % include retirement savings?</label>
                <select id="futureIncludesRetirement" className="fl-select" value={inputs.futureIncludesRetirement} onChange={update("futureIncludesRetirement")} data-testid="fl-input-future-includes">
                  <option value="yes">Yes — Future % already includes retirement</option>
                  <option value="no">No — retirement savings are separate</option>
                </select>
                <details>
                  <summary>What does this mean?</summary>
                  <div className="fl-small" style={{ marginTop: 6 }}>
                    Choose <strong>Yes</strong> if the Future % in your budget already includes retirement contributions, investing, or other long-term savings.
                    Choose <strong>No</strong> if your retirement savings rate is separate from the Future % amount in your take-home budget.
                  </div>
                </details>
              </div>
            </div>

            <div className="fl-card fl-section-card">
              <div className="fl-section-title">2) How are you positioning yourself for the future?</div>
              <p className="fl-panel-sub">A retirement projection based on your current path.</p>

              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, color: "var(--muted)" }}>
                  <span>Age-based savings benchmark</span>
                  <strong style={{ color: "var(--navy)" }} data-testid="fl-recommended-rate">{percent(r.recommendedRate, 0)}</strong>
                </div>
                <div className="fl-progress-track"><div className="fl-progress-fill" style={{ width: `${r.progressToTarget}%` }} /></div>
                <div className="fl-small" style={{ marginTop: 6 }}>A general educational benchmark only, not personalized advice.</div>
              </div>

              <div className="fl-compare-boxes">
                <div className="fl-compare">
                  <div className="t">Current path</div>
                  <div className="v" data-testid="fl-current-today">{currency(r.currentToday)}</div>
                  <div className="fl-small">In today's dollars</div>
                </div>
                <div className="fl-compare highlight">
                  <div className="t">If {r.extraSavingsPercent}% more is redirected</div>
                  <div className="v" data-testid="fl-plus-one-today">{currency(r.increasedToday)}</div>
                  <div className="fl-small">In today's dollars</div>
                </div>
              </div>
            </div>
          </div>

          <div className="fl-impact-section">
            <h2 data-testid="fl-section-3-title">3) How big of a difference could {r.extraSavingsPercent}% make?</h2>
            <p className="sub">See the upside of a small change and what it may actually feel like in your paycheck.</p>

            <div className="fl-impact-grid">
              <div className="fl-impact-box">
                <div className="label">Extra annual amount redirected</div>
                <div className="value" data-testid="fl-extra-annual">{currency(r.extraAnnualContribution)}</div>
                <div className="desc">That is the extra {r.extraSavingsPercent}% being sent toward retirement each year.</div>
              </div>
              <div className="fl-impact-box">
                <div className="label">Roth impact on take-home</div>
                <div className="value">{currency(r.rothTakeHomeReductionMonthly)}/mo</div>
                <div className="desc">About {currency(r.rothTakeHomeReductionAnnual)} per year since Roth is after-tax.</div>
              </div>
              <div className="fl-impact-box">
                <div className="label">Pre-tax impact on take-home</div>
                <div className="value">{currency(r.pretaxTakeHomeReductionMonthly)}/mo</div>
                <div className="desc">About {currency(r.pretaxTakeHomeReductionAnnual)} per year after estimated tax savings.</div>
              </div>
              <div className="fl-impact-box">
                <div className="label">Daily paycheck impact</div>
                <div className="value">{currency(r.pretaxTakeHomeReductionDaily)}/day</div>
                <div className="desc">
                  About {currency(r.rothTakeHomeReductionDaily)}/day if Roth, or {currency(r.pretaxTakeHomeReductionDaily)}/day pre-tax using a 30-day month.
                </div>
              </div>
            </div>

            <div className="fl-takeaway" data-testid="fl-takeaway">
              Simple takeaway: your Future allocation currently sends about <strong>{currency(r.futureAnnual)}</strong> per year toward future goals.
              On top of that, redirecting {r.extraSavingsPercent}% of income adds <strong>{currency(r.extraAnnualContribution)}</strong> more per year.
              This paycheck impact scales proportionally — 2% is about double, 3% about triple.
              If that extra goes to a Roth account, take-home pay generally drops by the full amount.
              If it goes to a pre-tax account, the paycheck impact is often closer to <strong>{currency(r.pretaxTakeHomeReductionMonthly)}</strong> per month, depending on your tax rate.
            </div>
          </div>
        </div>
      </section>

      {/* Next step / Reset CTA */}
      <section className="fl-next-step">
        <div className="fl-next-title">Based on your results, here's your next step</div>
        <div className="fl-next-sub">
          If you're thinking, "I should probably fix this," you're not looking for more random advice — you're looking for a better foundation.
        </div>

        <div className="fl-explain" data-testid="fl-explain-box">
          <strong>What this means:</strong><br /><br />
          You are currently directing about <strong>{currency(r.futureAnnual)}</strong> per year toward your future.<br />
          {inputs.futureIncludesRetirement === "yes"
            ? <>Because you selected <strong>Yes</strong>, the calculator assumes this Future % may already include retirement savings, so it avoids double counting where possible.<br /><br /></>
            : <>Because you selected <strong>No</strong>, the calculator treats your Future % as separate from your retirement savings rate, so both amounts are working toward your future.<br /><br /></>
          }
          At this pace, your projected retirement value is approximately <strong>{currency(r.currentToday)}</strong> in today's dollars.<br /><br />
          If you increase your future allocation or savings rate slightly, you could increase that to about <strong>{currency(r.increasedToday)}</strong> — a difference of <strong>{currency(r.extraImpactToday)}</strong>.<br /><br />
          This shows how small, manageable changes today can create meaningful long-term impact.
        </div>

        <div className="fl-explain" style={{ background: "#fff", border: "1px solid rgba(17,47,107,0.1)" }} data-testid="fl-budget-tie-in">
          <strong>Where does the extra {r.extraSavingsPercent}% come from?</strong><br /><br />
          Based on your inputs, redirecting just <strong>{currency(r.extraAnnualContribution)}</strong> per year could significantly improve your long-term outcome.<br /><br />
          The easiest place to find it is usually inside your budget — often by tightening Wants, optimizing Needs, or getting clearer on what your Future category already includes.<br /><br />
          <strong>The Financial Foundation Reset</strong> helps you sort that out step by step so your budget and savings plan actually work together in real life.
        </div>

        <div className="fl-actions-row">
          <button className="fl-btn" onClick={handleDownloadPdf} disabled={downloadingPdf} data-testid="fl-download-pdf">
            <Download size={16} />
            {downloadingPdf ? "Preparing PDF…" : "Download & email my report"}
          </button>
          <button className="fl-btn fl-btn-ghost" onClick={reset} data-testid="fl-reset-inputs">
            <RotateCcw size={16} />
            Reset inputs
          </button>
        </div>
        {lead?.email && (
          <div className="fl-actions-trust" data-testid="fl-email-trust" style={{
            textAlign: "center", fontSize: 13, color: "var(--muted)",
            marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <Mail size={14} /> We'll also email a copy to <strong style={{ color: "var(--navy)" }}>{lead.email}</strong>
          </div>
        )}

        <div className="fl-showcase">
          <div className="fl-showcase-content">
            <div className="fl-reset-kicker">Financial Foundation Reset</div>
            <h3 className="fl-reset-headline">Build a budget that actually works — and finally make your money support the life you want.</h3>
            <p className="fl-reset-copy">
              <strong>The Financial Foundation Reset</strong> doesn't just change how you manage money — it changes how you experience your life,
              because money stops being the obstacle and starts becoming a tool.
            </p>
            <div className="fl-reset-points">
              <div className="fl-reset-point">Science-backed mindset work</div>
              <div className="fl-reset-point">A practical budgeting system</div>
              <div className="fl-reset-point">Live support to help it stick</div>
            </div>
            <p className="fl-reset-copy">
              When a strong money mindset is paired with a practical budgeting system, taking control of your money — and building lasting financial success — becomes inevitable.<br /><br />
              This three-week, guided experience helps you feel calm, confident, and in control of your money.<br /><br />
              You don't need more advice. You need a foundation. And this is where it begins.
            </p>
            <div style={{ textAlign: "center", marginTop: 18 }}>
              <a className="fl-showcase-cta" href={RESET_CTA_URL} target="_blank" rel="noopener noreferrer" data-testid="fl-cta-button">
                Click here to learn how to find your 1% → Financial Foundation Reset
              </a>
              <div className="fl-showcase-subcta">Small shifts. Strong foundation. Lasting change.</div>
            </div>
          </div>
        </div>
      </section>

      <div className="fl-footer-note">
        <strong>Educational use only</strong>
        <ul>
          <li>This tool is for general financial education — not personalized advice.</li>
          <li>Results are estimates based on simplified assumptions.</li>
          <li>It does not include employer match, Social Security, pensions, or full tax details.</li>
          <li>Actual results will vary based on your situation and investments.</li>
        </ul>
      </div>

      {emailToast && (
        <div className={`fl-toast ${emailToast.kind === "error" ? "error" : ""}`} data-testid="fl-email-toast">
          {emailToast.msg}
        </div>
      )}
    </div>
  );
}
